import { useMemo } from "react";
import { generatePath, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { CALENDAR_TRIAL_DAY_ROUTE } from "../../../app/router/routes";
import { ITrialCalendarFilter } from "../../../entities/trialLesson";
import { useAppSelector } from "../../../app/store/store";
import { FEATURES, hasFeature } from "../../../entities/my";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { TrialCalendarMonth } from "../../../widgets/trialCalendarMonth";
import {
	clearTrialCreationSearchParams,
	getTrialCreationDraftFromSearchParams,
	isTrialCalendarCreationFilterComplete,
	isTrialCreationDraftComplete,
	setTrialCreationDraftSearchParams,
	TrialCreationSetup,
	trialCreationDraftToFilter,
} from "../../../features/trialLessonCreate";
import classes from './calendarTrial.module.scss'

interface ICalendarOutletContext {
    trialFilter: ITrialCalendarFilter;
}

const getDateParam = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

export default function CalendarTrialPage() {

    const {trialFilter} = useOutletContext<ICalendarOutletContext>()
	const {my} = useAppSelector(state => state.myReducer)
	const navigate = useNavigate()
	const location = useLocation()
	const creationMode = new URLSearchParams(location.search).get('createTrial') === '1'
	const creationDraft = useMemo(
		() => getTrialCreationDraftFromSearchParams(new URLSearchParams(location.search)),
		[location.search],
	)
	const creationFilter = useMemo(
		() => trialCreationDraftToFilter(creationDraft),
		[creationDraft],
	)
	const canCreateByAccess = hasFeature(my, FEATURES.TRIAL_LESSONS.CREATE) &&
		hasFeature(my, FEATURES.TRIAL_LESSONS.SLOT_CREATE)
	const canCreateTrial = canCreateByAccess && isTrialCreationDraftComplete(creationDraft)
	const legacyCreationFilter = canCreateByAccess && isTrialCalendarCreationFilterComplete(trialFilter)
		? trialFilter
		: undefined
	const effectiveCreationFilter = creationMode ? creationFilter : legacyCreationFilter

	const setCreationMode = (enabled: boolean) => {
		let params = new URLSearchParams(location.search)
		if(enabled) params.set('createTrial', '1')
		else params = clearTrialCreationSearchParams(params)

		navigate({pathname: location.pathname, search: params.toString()}, {replace: true})
	}

    const openDay = (date: Date) => {
        navigate({
            pathname: generatePath(CALENDAR_TRIAL_DAY_ROUTE.path, {
                date: getDateParam(date),
            }),
            search: location.search,
        })
    }

	return (
		<section className={classes.container}>
			{canCreateByAccess && (
				<section className={classes.creationPanel}>
					<section className={classes.creationContent}>
						<h2>Создание пробного занятия</h2>
						<p>
							{canCreateTrial
								? creationMode
									? 'Выберите доступный день, затем отметьте время занятия.'
									: 'Параметры заполнены. Исполнителя выберем после времени.'
								: 'Выберите один рабочий Telegram, один тег и желаемый пол исполнителя.'
							}
						</p>
						<TrialCreationSetup
							value={creationDraft}
							disabled={creationMode}
							onChange={draft => {
								const params = setTrialCreationDraftSearchParams(
									new URLSearchParams(location.search),
									draft,
								)
								navigate({pathname: location.pathname, search: params.toString()}, {replace: true})
							}}
						/>
					</section>
					<section className={classes.creationButton}>
						{creationMode ? (
							<MyButtonSecondary onClick={() => setCreationMode(false)}>Отменить создание</MyButtonSecondary>
						) : (
							<MyButton disabled={!canCreateTrial} onClick={() => setCreationMode(true)}>
								Создать пробное
							</MyButton>
						)}
					</section>
				</section>
			)}
			<TrialCalendarMonth
				filter={trialFilter}
				creationFilter={effectiveCreationFilter}
				onSelectDate={openDay}
			/>
		</section>
	)
}
